import { css } from "@emotion/css";
import { PopoverBasicHelp, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { Repository, SpaceDefaultMachinePackageCacheRetentionPolicyResource } from "@octopusdeploy/octopus-server-client";
import { MachinePackageCacheRetentionUnit, Permission, MachinePackageCacheConstants, MachinePackageCacheRetentionStrategy } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React, { useEffect } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { RedirectAs404 } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { Form, useForm } from "~/components/form/Form/Form";
import { FormSection, minimum, Text } from "~/components/form/index";
export async function packageCacheRetentionPageLoader(repository: Repository): Promise<PackageCacheRetentionPageLoaderData> {
    const defaultPolicy = repository.SpaceDefaultRetentionPolicies.getDefaultByType("MachinePackageCache");
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { policy: (await defaultPolicy) as SpaceDefaultMachinePackageCacheRetentionPolicyResource };
}
export interface PackageCacheRetentionPageLoaderData {
    policy: SpaceDefaultMachinePackageCacheRetentionPolicyResource;
}
interface PackageCacheRetentionPoliciesPageProps {
    spaceId: string;
    loaderData: PackageCacheRetentionPageLoaderData;
}
export function PackageCacheRetentionPoliciesPage({ spaceId, loaderData }: PackageCacheRetentionPoliciesPageProps) {
    const pageTitle = "Package Cache Retention";
    const [packagesToKeepStrategy, setPackagesToKeepStrategy] = React.useState<string>(loaderData.policy.QuantityOfPackagesToKeep && loaderData.policy.QuantityOfPackagesToKeep > 0 ? "limited" : "all");
    const { model, isSubmitting, setModel, formProps, createSaveAction, submit } = useForm({
        formName: "Save default package cache retention policy",
        initialModel: loaderData.policy,
        onSubmit: async (repository, policy) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return (await repository.SpaceDefaultRetentionPolicies.updateDefault(policy)) as SpaceDefaultMachinePackageCacheRetentionPolicyResource;
        },
        snackbarSaveText: "Default package cache retention policy updated",
        submitPermission: { permission: [Permission.RetentionAdminister] },
        updateFormModelAfterSubmit: (model) => model,
    });
    useEffect(() => {
        if (model.Strategy === MachinePackageCacheRetentionStrategy.FreeSpace) {
            setModel((prev) => ({
                ...prev,
                Strategy: MachinePackageCacheRetentionStrategy.FreeSpace,
                QuantityOfVersionsToKeep: undefined,
                VersionUnit: undefined,
                QuantityOfPackagesToKeep: undefined,
                PackageUnit: undefined,
            }));
        }
        else if (model.Strategy === MachinePackageCacheRetentionStrategy.Quantities && packagesToKeepStrategy === "all") {
            setModel((prev) => ({
                ...prev,
                Strategy: MachinePackageCacheRetentionStrategy.Quantities,
                QuantityOfVersionsToKeep: model.QuantityOfVersionsToKeep ?? MachinePackageCacheConstants.DefaultVersionsToKeep,
                VersionUnit: MachinePackageCacheRetentionUnit.Items,
                QuantityOfPackagesToKeep: MachinePackageCacheConstants.KeepAll,
                PackageUnit: undefined,
            }));
        }
        else if (model.Strategy === MachinePackageCacheRetentionStrategy.Quantities && packagesToKeepStrategy === "limited") {
            setModel((prev) => ({
                ...prev,
                Strategy: MachinePackageCacheRetentionStrategy.Quantities,
                QuantityOfVersionsToKeep: model.QuantityOfVersionsToKeep ?? MachinePackageCacheConstants.DefaultVersionsToKeep,
                VersionUnit: MachinePackageCacheRetentionUnit.Items,
                QuantityOfPackagesToKeep: getPackageValue(),
                PackageUnit: MachinePackageCacheRetentionUnit.Items,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model.Strategy, model.QuantityOfVersionsToKeep, packagesToKeepStrategy]);
    if (!isFeatureToggleEnabled("centralized-retention-v2")) {
        return <RedirectAs404 />;
    }
    function getPackageValue() {
        return model.QuantityOfPackagesToKeep && model.QuantityOfPackagesToKeep > 0 ? model.QuantityOfPackagesToKeep : MachinePackageCacheConstants.DefaultPackagesToKeep;
    }
    const styles = {
        formSectionTitleContainer: css({
            display: "flex",
            flexWrap: "nowrap",
            flexDirection: "row",
            alignItems: "center",
            gap: space[4],
        }),
        textInput: css({
            width: "35%",
        }),
    };
    return (<PageContent header={{
            title: pageTitle,
            primaryAction: createSaveAction(),
            showBreadcrumbBackIcon: true,
            breadcrumbs: [{ label: "Retention Policies", pageUrl: links.retentionPoliciesPage.generateUrl({ spaceId }) }],
        }}>
            <BorderedContent>
                <Form {...formProps}>
                    <FormSection title={<span className={styles.formSectionTitleContainer}>
                                Default Retention Policy
                                <PopoverBasicHelp description="This retention policy will be applied to all packages that use the “Default retention policy”" placement="right-start"/>
                            </span>} help="This retention policy dictates how long packages are kept on targets and workers.">
                        <RadioButtonGroup title="How long should we keep packages on deployment targets and workers?" value={model.Strategy} onChange={(strategy) => setModel((prev) => ({ ...prev, Strategy: strategy }))} accessibleName="Retention Strategy">
                            <RadioButton value={MachinePackageCacheRetentionStrategy.FreeSpace} label="Let Octopus decide"/>
                            {isFeatureToggleEnabled("configurable-package-cache-retention") && (<>
                                    <RadioButton value={MachinePackageCacheRetentionStrategy.Quantities} label="Keep a limited number"/>
                                    {model.Strategy === MachinePackageCacheRetentionStrategy.Quantities && (<div>
                                            <Text className={styles.textInput} type="number" min={MachinePackageCacheConstants.MinVersionsToKeep} label="Versions" accessibleName="Number of versions to keep" value={(model.QuantityOfVersionsToKeep ?? MachinePackageCacheConstants.DefaultVersionsToKeep).toString()} onChange={(versionsToKeep) => setModel((prev) => ({ ...prev, QuantityOfVersionsToKeep: parseInt(versionsToKeep) }))} validate={minimum("Quantity of versions to keep must be a greater than 1", MachinePackageCacheConstants.MinVersionsToKeep)}/>

                                            <RadioButtonGroup value={packagesToKeepStrategy} onChange={(x) => setPackagesToKeepStrategy(x)} accessibleName="Packages to keep">
                                                <RadioButton value="all" label="From all packages used"/>
                                                <RadioButton value="limited" label="From the most recently used packages"/>

                                                {packagesToKeepStrategy === "limited" && (<Text className={styles.textInput} type="number" min={MachinePackageCacheConstants.MinVersionsToKeep} label="Packages" accessibleName="Number of packages to keep" value={(model.QuantityOfPackagesToKeep ?? MachinePackageCacheConstants.DefaultPackagesToKeep).toString()} onChange={(quantityOfPackages) => setModel((prev) => ({ ...prev, QuantityOfPackagesToKeep: parseInt(quantityOfPackages) }))} validate={minimum("Quantity of packages to keep must be a greater than 1", MachinePackageCacheConstants.MinVersionsToKeep)}/>)}
                                            </RadioButtonGroup>
                                        </div>)}
                                </>)}
                        </RadioButtonGroup>
                    </FormSection>
                </Form>
            </BorderedContent>
        </PageContent>);
}
